<template>
    <div class="my-4">
        <div
            v-if="loading"
            class="text-sm italic text-gray-400"
        >
            {{ i18n.t('loading') }}
        </div>
        <div
            v-else-if="openRounds.length === 0"
            class="text-sm italic text-gray-400"
        >
            {{ i18n.t('help_no_open_acceptance_round') }}
        </div>
        <div
            v-else
            class="space-y-2"
        >
            <div
                v-for="round in openRounds"
                :key="round.round_id"
                class="flex items-center justify-between gap-4 rounded border border-gray-200 px-3 py-2"
            >
                <label
                    class="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
                >
                    <input
                        v-model="selectedRoundId"
                        type="radio"
                        name="acceptance_round"
                        :value="round.round_id"
                    />
                    {{ roundLabel(round) }}
                </label>
                <label
                    class="flex items-center gap-2 text-sm"
                    :class="
                        isCloseDisabled(round)
                            ? 'text-gray-400'
                            : 'cursor-pointer text-gray-600'
                    "
                >
                    <input
                        type="checkbox"
                        :checked="pendingCloseIds.includes(round.round_id)"
                        :disabled="isCloseDisabled(round)"
                        @change="
                            toggleClose(
                                round.round_id,
                                ($event.target as HTMLInputElement).checked
                            )
                        "
                    />
                    {{ i18n.t('mark_acceptance_round_done') }}
                </label>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { Asset } from '@src/types'
import { AcceptanceRound, getAcceptanceRounds } from '@src/utils/api/assets'
import { formatNumericValues } from '@src/utils/helpers'
import { useVModels } from '@vueuse/core'
import moment from 'moment'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    asset: Asset
    modelValue: number | null
}>()

const emit = defineEmits([
    'update:modelValue',
    'update:selectedRound',
    'update:roundsToClose',
])

const i18n = useI18n()
const { modelValue: selectedRoundId } = useVModels(props, emit)

const loading = ref(true)
const rounds = ref<AcceptanceRound[]>([])
const pendingCloseIds = ref<number[]>([])

// Oldest first, latest last — the round an admin most likely wants to act on
// next reads naturally at the bottom of the list.
const openRounds = computed(() =>
    rounds.value
        .filter((r) => !r.closed_at)
        .sort((a, b) => a.round_id - b.round_id)
)

// The most recently accepted round is very likely still mid-workflow (other
// actions may not have run against it yet), so it can never be marked done —
// whether it's the only open round or the newest among several.
const latestRoundId = computed(() =>
    openRounds.value.length > 0
        ? Math.max(...openRounds.value.map((r) => r.round_id))
        : null
)

// A round can't be marked done while it's the latest (very likely still
// mid-workflow) or while it's the currently selected action target (running
// a new action against it means it isn't done).
function isCloseDisabled(round: AcceptanceRound): boolean {
    return (
        round.round_id === latestRoundId.value ||
        round.round_id === selectedRoundId.value
    )
}

function roundLabel(round: AcceptanceRound): string {
    return i18n.t('acceptance_round_label', {
        date: round.accepted_at
            ? moment(round.accepted_at).format('DD.MM.yyyy')
            : '-',
        count: round.order_count,
        amount: formatNumericValues(
            Number(round.total_amount),
            props.asset.currency ?? undefined
        ),
    })
}

function toggleClose(roundId: number, checked: boolean) {
    pendingCloseIds.value = checked
        ? [...pendingCloseIds.value, roundId]
        : pendingCloseIds.value.filter((id) => id !== roundId)
}

watch(pendingCloseIds, (ids) => emit('update:roundsToClose', ids))

// Selecting a round as the action's target and marking it "done" are mutually
// exclusive — if it was checked done, running a new action against it means
// it isn't, so drop it from the pending-close set.
watch(selectedRoundId, (roundId) => {
    if (roundId !== null && pendingCloseIds.value.includes(roundId)) {
        pendingCloseIds.value = pendingCloseIds.value.filter(
            (id) => id !== roundId
        )
    }
})

watch(
    [selectedRoundId, rounds],
    () => {
        emit(
            'update:selectedRound',
            rounds.value.find((r) => r.round_id === selectedRoundId.value) ??
                null
        )
    },
    { immediate: true }
)

onMounted(fetchRounds)

async function fetchRounds() {
    loading.value = true
    const response = await getAcceptanceRounds(props.asset)
    rounds.value = response.data
    if (openRounds.value.length === 1) {
        selectedRoundId.value = openRounds.value[0].round_id
    }
    loading.value = false
}
</script>
